/**
 * Component-owned performance instrumentation.
 *
 * @remarks
 *
 * Page-level Core Web Vitals are a joint number with the Drupal host and can't
 * see inside the embed's shadow root, so the component emits its own
 * `performance.mark()` / `measure()` pairs for the phases it does own.
 *
 * Every measure is recorded **at most once per page load** — these describe the
 * initial boot, not steady-state navigation. Once a measure completes, later
 * calls are no-ops, which also makes them safe under React StrictMode's
 * double-invoked effects.
 *
 * Results are readable via `performance.getEntriesByType('measure')` and show
 * up in the DevTools Performance timeline.
 */

export const MDC_MEASURE = {
  /** `connectedCallback` → React's first commit. */
  boot: 'mdc:boot',
  /** React's first commit → detail-page data rendered (skeletons replaced). */
  dataVisible: 'mdc:data-visible',
  /** Chart data resolved → chart SVG committed. */
  chartRender: 'mdc:chart-render',
} as const;

export type MdcMeasure = (typeof MDC_MEASURE)[keyof typeof MDC_MEASURE];

const startedMeasures = new Set<MdcMeasure>();
const completedMeasures = new Set<MdcMeasure>();

const startMarkName = (measure: MdcMeasure) => `${measure}:start`;
const endMarkName = (measure: MdcMeasure) => `${measure}:end`;

function isSupported() {
  return (
    typeof performance !== 'undefined' &&
    typeof performance.mark === 'function' &&
    typeof performance.measure === 'function'
  );
}

/**
 * Opens a measure by recording its start mark.
 *
 * @returns `true` if the start mark was recorded, `false` if the measure was
 * already opened or completed (or the Performance API is unavailable).
 */
export function startMeasure(measure: MdcMeasure): boolean {
  if (
    !isSupported() ||
    startedMeasures.has(measure) ||
    completedMeasures.has(measure)
  ) {
    return false;
  }

  performance.mark(startMarkName(measure));
  startedMeasures.add(measure);

  return true;
}

/**
 * Closes a previously opened measure and records the `performance.measure()`
 * entry. No-ops if the measure was never opened or has already completed.
 *
 * @returns `true` if the measure entry was recorded.
 */
export function endMeasure(measure: MdcMeasure): boolean {
  if (
    !isSupported() ||
    !startedMeasures.has(measure) ||
    completedMeasures.has(measure)
  ) {
    return false;
  }

  const endMark = endMarkName(measure);

  performance.mark(endMark);
  performance.measure(measure, startMarkName(measure), endMark);
  completedMeasures.add(measure);

  return true;
}

/** Whether a measure has already been recorded this page load. */
export function hasCompletedMeasure(measure: MdcMeasure): boolean {
  return completedMeasures.has(measure);
}

/**
 * Clears recorded state so each test starts from a clean slate. Not used in
 * production — a real page load only ever boots once.
 */
export function resetMeasures(): void {
  startedMeasures.clear();
  completedMeasures.clear();

  if (isSupported()) {
    performance.clearMarks();
    performance.clearMeasures();
  }
}
